/**
 * Discount Timer Utility
 *
 * Manages 30-minute countdown timer for first-month discount offers.
 * Timer is based on StoreModel.installedAt (database timestamp) for cross-device consistency.
 *
 * Key Architecture:
 * - Timer start time: StoreModel.installedAt (NOT localStorage)
 * - Banner dismissal: localStorage (device-specific UI preference)
 * - Cross-device consistent: Same countdown on all devices
 *
 * @module discountTimer
 */

const TIMER_DURATION_MS = 30 * 60 * 1000; // 30 minutes in milliseconds

/**
 * Calculate remaining time based on installation timestamp
 * Uses StoreModel.installedAt as the timer start time
 *
 * @param installedAt - App installation timestamp from StoreModel.installedAt
 * @returns Remaining milliseconds (0 if expired, capped at 30 minutes max)
 *
 * @example
 * // Store installed at 2:00 PM, current time 2:15 PM
 * const remaining = getRemainingTime(store.installedAt);
 * // Returns: 900000 (15 minutes in milliseconds)
 *
 * @example
 * // Cross-device consistency
 * // Desktop at 2:10 PM: getRemainingTime(installedAt) → 20 minutes
 * // Mobile at 2:10 PM: getRemainingTime(installedAt) → 20 minutes (same!)
 *
 * @example
 * // Future timestamp (clock skew or bad data)
 * const futureDate = new Date(Date.now() + 60000); // 1 minute in future
 * getRemainingTime(futureDate); // Returns: 1800000 (full 30 minutes)
 */
export function getRemainingTime(installedAt: Date | string): number {
  // Convert to timestamp if Date object or string
  const startTime = installedAt instanceof Date
    ? installedAt.getTime()
    : new Date(installedAt).getTime();

  // Validate that we have a valid timestamp
  if (!Number.isFinite(startTime) || Number.isNaN(startTime)) {
    // Invalid date - return 0 (timer expired)
    return 0;
  }

  // Clamp elapsed to non-negative (handles future timestamps)
  const elapsed = Math.max(0, Date.now() - startTime);
  const remaining = TIMER_DURATION_MS - elapsed;

  // Clamp to [0, TIMER_DURATION_MS] range
  return Math.min(TIMER_DURATION_MS, Math.max(0, remaining));
}

/**
 * Check if timer has expired based on installation timestamp
 *
 * @param installedAt - App installation timestamp from StoreModel.installedAt
 * @returns true if >= 30 minutes have elapsed since installation
 *
 * @example
 * // Check if offer is still valid
 * if (!isTimerExpired(store.installedAt)) {
 *   // Show discount offer
 * }
 */
export function isTimerExpired(installedAt: Date | string): boolean {
  return getRemainingTime(installedAt) <= 0;
}

/**
 * Format milliseconds into countdown string
 * Stateless formatter - works with any remaining time value
 *
 * @param remainingMs - Milliseconds remaining
 * @returns Formatted string "23m 45s" (never negative)
 *
 * @example
 * formatCountdown(90000)  // "1m 30s"
 * formatCountdown(1500)   // "0m 1s"
 * formatCountdown(-1000)  // "0m 0s" (clamped to zero)
 */
export function formatCountdown(remainingMs: number): string {
  // Defensive check: clamp negative values to 0
  const clampedMs = Math.max(0, remainingMs);

  const minutes = Math.floor(clampedMs / 60000);
  const seconds = Math.floor((clampedMs % 60000) / 1000);

  return `${minutes}m ${seconds}s`;
}

/**
 * Get color code based on remaining time
 * Used for visual urgency indicators in countdown display
 *
 * @param remainingMs - Milliseconds remaining
 * @returns Color code: 'green' (>10min), 'orange' (5-10min), 'red' (<5min)
 *
 * @example
 * const color = getColorCode(remainingMs);
 * // Pass to banner component: <Banner :countdownColor="color" />
 */
export function getColorCode(remainingMs: number): 'green' | 'orange' | 'red' {
  const minutes = remainingMs / 60000;

  if (minutes > 10) return 'green';
  if (minutes > 5) return 'orange';
  return 'red';
}